import React from "react";
import { inject, observer } from "mobx-react";
import {
  Grid,
  Row,
  Col,
  MenuItem,
  DropdownButton,
  Collapse,
} from "react-bootstrap";
import { StoreContext } from "../../contexts/StoreContext";
import { CubeRoleGroupDifferences } from "./CubeRoleGroupDifferences";
import ComparableObjectList from "./ComparableObjectList";
import DetailOfComparisonItem from "./DetailOfComparisonItem";

@inject("compareCubeStore", "searchStore")
@observer
export class CubeCompare extends React.Component {
  state = {
    listOfComparableObjects: null,
    totalCount: 0,
    detailOfSelectedItem: null,
    versionsOfSelectedItem: null,
    listOfDiffs: null,
    selectedVersion: null,
    validFromDate: null,
    validToDate: null,
    selectedFrameworkId: this.props.object.frameworkId || null,
    isComponentOpen: true,
  };

  async componentDidMount() {
    this.findPossibleObjectsForComparison()
  }

  findPossibleObjectsForComparison = async (limit = 10, offset = 0, searchExpr) => {
    const { fetchPossibleObjectsForComparison } = this.props.compareCubeStore;
    const objectType = this.props.object.type;
    fetchPossibleObjectsForComparison(objectType, this.state.selectedFrameworkId, this.props.object.tid, limit, offset, searchExpr).then((result) => {
      this.setState({ 
        listOfComparableObjects: result.objectsResults,
        totalCount: result.totalCount
       });
    });
  }

  fetchItemData = (itemData) => {
    const { getAllObjectVersions } = this.props.compareCubeStore;
    const objectType = this.props.object.type;
    const itemCode = itemData?.code;
    if (!itemData) return;
    getAllObjectVersions(objectType, itemCode).then((results) => {
      if (
        results.includes(this.props.object.version) &&
        results.findIndex((element) => element === this.props.object.version) >=
          1
      ) {
        this.setState(
          {
            versionsOfSelectedItem: results,
            selectedVersion:
              results[
                results.findIndex(
                  (element) => element === this.props.object.version
                ) - 1
              ],
            detailOfSelectedItem: itemData,
          },
          this.fetchItemDiff
        );
      } else
        this.setState(
          {
            versionsOfSelectedItem: results,
            selectedVersion: results[results.length - 1],
            detailOfSelectedItem: itemData,
          },
          this.fetchItemDiff
        );
    });
  };

  fetchItemDiff = () => {
    const {
      validFromDate,
      validToDate,
      selectedVersion,
      detailOfSelectedItem,
    } = this.state;
    const { getDeepChanges } = this.props.compareCubeStore;
    const baseObject = this.props.object;

    if (selectedVersion !== null || (validFromDate && validToDate)) {
      const comparedObjectInfo = {
        type: baseObject.type,
        code: detailOfSelectedItem.code,
        version: selectedVersion,
        validFrom: validFromDate,
        validTo: validToDate,
      };

      getDeepChanges(baseObject, comparedObjectInfo).then((results) => {
        this.setState({ listOfDiffs: results });
      });
    }
  };

  handleVersionSelect = (version) => {
    this.setState(
      { selectedVersion: version, validFromDate: null, validToDate: null },
      this.fetchItemDiff
    );
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date, selectedVersion: null }, () => {
      if (this.state.validFromDate && this.state.validToDate) {
        this.fetchItemDiff();
      }
    });
  };
  handleFrameworkChange = (frameworkId) => {
    if (this.state.selectedFrameworkId === frameworkId) {
      return;
    }

    this.setState({ selectedFrameworkId: frameworkId }, () => this.findPossibleObjectsForComparison());
  };
  getFrameworkOptions = (frameworks) => {
    if (!frameworks || frameworks.length < 1) return null;
    return frameworks
      .sort((f1, f2) => {
        if (f1.name > f2.name) return 1;
        if (f2.name > f1.name) return -1;
        return 0;
      })
      .map((framework) => {
        return (
          <MenuItem
            key={framework.code}
            value={framework.frameworkId}
            onSelect={() => {
              this.handleFrameworkChange(framework.frameworkId);
            }}
            active={this.state.selectedFrameworkId === framework.frameworkId}
            title={framework.code}
            className={
              framework.type === "Framework"
                ? framework.maintenanceAgencyId === "ECB"
                  ? "ref"
                  : "nonref"
                : ""
            }
          >
            {framework.name}
          </MenuItem>
        )
      });
  };

  static contextType = StoreContext;

  render() {
    const {
      versionsOfSelectedItem,
      selectedVersion,
      listOfDiffs,
      detailOfSelectedItem,
      validFromDate,
      validToDate,
      selectedFrameworkId,
      listOfComparableObjects,
      totalCount,
      isComponentOpen,
    } = this.state;
    const { getMacroFrameworksMaintenanceAgencies, getFrameworks } =
      this.props.searchStore;
    let dimensions = null;
    let observations = null;
    let attributes = null;

    if (this.props.object.type === "Cube") {
      dimensions = listOfDiffs?.changesResults?.filter(
        (item) => item?.object?.role.toLowerCase() === "d"
      );
      observations = listOfDiffs?.changesResults?.filter(
        (item) => item.object.role.toLowerCase() === "o"
      );
      attributes = listOfDiffs?.changesResults?.filter(
        (item) => item.object.role.toLowerCase() === "a"
      );
    }

    let frameworkOptions = getFrameworks;

    const maintenanceAgenciesToExclude = getMacroFrameworksMaintenanceAgencies;
    const ref = this.getFrameworkOptions(
      frameworkOptions.filter(
        (framework) =>
          framework.type === "Framework" &&
          framework.maintenanceAgencyId === "ECB" &&
          !maintenanceAgenciesToExclude.includes(framework.maintenanceAgencyId)
      )
    );

    const nonref = this.getFrameworkOptions(
      frameworkOptions.filter(
        (framework) =>
          framework.type === "Framework" &&
          framework.maintenanceAgencyId !== "ECB" &&
          !maintenanceAgenciesToExclude.includes(framework.maintenanceAgencyId)
      )
    );

    const groups = this.getFrameworkOptions(
      frameworkOptions.filter(
        (framework) => framework.type === "FrameworkGroup"
      )
    );

    return (
      <div ref={this.props.innerRef} className="related-objects custom-panel">
        <div
          className="custom-panel-heading"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h4>{this.props.object.type} Comparison</h4>
        </div>
        <Collapse in={isComponentOpen}>
          <div>
            {isComponentOpen ? (
              <>
                {listOfComparableObjects  ? (
                  <div className="custom-panel-body">
                    <div
                      style={{
                        marginBlock: "5px",
                      }}
                    >
                      <div>Framework:</div>
                      <DropdownButton
                        closeonchange={"true"}
                        id={"framework-search"}
                        title={(() => {
                          let label = "All applicable Frameworks";
                          if (selectedFrameworkId) {
                            const target = frameworkOptions.find(framework => framework.frameworkId === selectedFrameworkId);
                            if (target) {
                              label = target.name;
                            }
                          }
                          return label;
                        })()}
                      >
                        <MenuItem
                          key={"all"}
                          onSelect={() => this.handleFrameworkChange(null)}
                          active={!this.state.selectedFrameworkId}
                        >
                          All applicable Frameworks
                        </MenuItem>
                        {ref ? <MenuItem divider /> : null}
                        {ref}
                        {nonref ? <MenuItem divider /> : null}
                        {nonref}
                        {groups ? <MenuItem divider /> : null}
                        {groups}
                      </DropdownButton>
                    </div>
                    <ComparableObjectList
                      onSelectItem={this.fetchItemData}
                      onPaginationChange={this.findPossibleObjectsForComparison}
                      comparableObject={listOfComparableObjects}
                      totalCount={totalCount}
                      frameworkId={this.state.selectedFrameworkId}
                    />
                    <div>
                      <DetailOfComparisonItem
                        detailOfSelectedItem={detailOfSelectedItem}
                        versionsOfSelectedItem={versionsOfSelectedItem}
                        selectedVersion={selectedVersion}
                        validFromDate={validFromDate}
                        validToDate={validToDate}
                        onVersionSelect={this.handleVersionSelect}
                        onDateChange={this.handleDateChange}
                      />
                    </div>
                  </div>
                ) : null}

                {listOfDiffs ? (
                  <div className="custom-panel-body related-types-list">
                    <Grid className="cube-structure-item" fluid={true}>
                      <Row>
                        <Col xs={6}>
                          <h5 style={{ marginLeft: "10px" }}>
                            <b>
                              {this.props.object.type === "Cube"
                                ? "Attributes"
                                : "Variable"}
                            </b>
                          </h5>
                        </Col>
                        <Col xs={6}>
                          <h5>
                            <b>With values restricted to</b>
                          </h5>
                        </Col>
                      </Row>
                    </Grid>
                    {this.props.object.type === "Cube" && (
                      <>
                        {dimensions && dimensions.length ? (
                          <CubeRoleGroupDifferences
                            type="d"
                            items={dimensions}
                            searchExpr={null}
                          />
                        ) : null}
                        {observations && observations.length ? (
                          <CubeRoleGroupDifferences
                            type="o"
                            items={observations}
                            searchExpr={null}
                          />
                        ) : null}
                        {attributes && attributes.length ? (
                          <CubeRoleGroupDifferences
                            type="a"
                            items={attributes}
                            searchExpr={null}
                          />
                        ) : null}
                      </>
                    )}
                    {this.props.object.type === "Combination" && (
                      <CubeRoleGroupDifferences
                        type=""
                        items={listOfDiffs.changesResults}
                        searchExpr={null}
                      />
                    )}
                  </div>
                ) : detailOfSelectedItem ? (
                  "No result Found"
                ) : null}
              </>
            ) : (
              ""
            )}
          </div>
        </Collapse>
      </div>
    );
  }
}
